import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import type { UserListItem } from '../../types/admin';
import {
  AdminEmpty,
  AdminLoading,
  AdminPageHeader,
  UserActiveBadge,
  confirmAction,
  formatDate,
} from './adminShared';
import './AdminLayout.css';

const AdminUsersList: React.FC = () => {
  const [items, setItems] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'true' | 'false'>('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const filters: { q?: string; active?: boolean } = {};
      if (q.trim()) filters.q = q.trim();
      if (activeFilter === 'true') filters.active = true;
      if (activeFilter === 'false') filters.active = false;

      const data = await adminApi.listUsers(filters);
      setItems(data.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error cargando usuarios');
    } finally {
      setLoading(false);
    }
  }, [q, activeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = async (user: UserListItem) => {
    const nextActive = !user.active;
    const msg = nextActive
      ? `¿Reactivar a ${user.email}?`
      : `¿Deshabilitar a ${user.email}? No podrá iniciar sesión.`;
    if (!confirmAction(msg)) return;

    try {
      await adminApi.setUserActive(user.id, nextActive);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error actualizando usuario');
    }
  };

  return (
    <>
      <AdminPageHeader title="Usuarios" actionLabel="+ Nuevo usuario" actionTo="/admin/users/new" />

      <div className="admin-toolbar">
        <input
          className="admin-input"
          placeholder="Buscar por email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select
          className="admin-select"
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value as 'all' | 'true' | 'false')}
        >
          <option value="all">Todos</option>
          <option value="true">Activos</option>
          <option value="false">Inactivos</option>
        </select>
        <button type="button" className="admin-btn admin-btn-secondary" onClick={load}>
          Buscar
        </button>
      </div>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      {loading ? (
        <AdminLoading />
      ) : items.length === 0 ? (
        <AdminEmpty message="No se encontraron usuarios." />
      ) : (
        <div className="admin-card admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Tienda</th>
                <th>Estado</th>
                <th>Creado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((user) => (
                <tr key={user.id}>
                  <td>{user.email}</td>
                  <td>
                    {user.store_id ? (
                      <Link to={`/admin/stores/${user.store_id}`}>
                        {user.store_name || user.store_id}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <UserActiveBadge active={user.active} />
                  </td>
                  <td>{formatDate(user.created_at)}</td>
                  <td>
                    <button type="button" className="admin-btn-link" onClick={() => handleToggle(user)}>
                      {user.active ? 'Deshabilitar' : 'Habilitar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default AdminUsersList;
